import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  collection,
  doc,
  updateDoc,
  serverTimestamp,
  deleteField,
  getDocs,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import logo from './pacePackPrime.png';
import {
  Box,
  Paper,
  ButtonBase,
  Stack,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Chip,
  Link,
  Skeleton,
  Typography,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  KeyboardReturnOutlined as ReturnedIcon,
  PaymentsOutlined as ValueIcon,
  PercentOutlined as RateIcon,
  CheckCircleOutlined as VerifiedIcon,
  ScheduleOutlined as OutstandingIcon,
} from '@mui/icons-material';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Only these two orderings are backed by an existing composite index
// (status + createdAt, status + trackingNumber). Sorting on any other field would
// fail at query time until a matching index is deployed.
const SORTABLE_FIELDS = ['createdAt', 'trackingNumber'];

const numberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatCurrency = (amount) => `TT$ ${numberFormat.format(amount || 0)}`;

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  // A just-written verification is patched into the cache as a real Date before
  // the server timestamp comes back, so it has to render too.
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  return null;
};

const formatDateTime = (value) => {
  const date = toDate(value);
  return date
    ? date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
};

// A return is verified once someone has confirmed the goods actually came back.
// Mirrors the verified/outstanding language and colours the Reconciliation page
// uses for payments, so the two review flows read the same way.
//
// Packages carry no field until they are verified, so absent means outstanding.
const isReturnVerified = (pkg) => pkg.returnVerified === true;

const VERIFICATION_META = {
  verified: {
    label: 'Verified',
    Icon: VerifiedIcon,
    sx: { bgcolor: '#dcfce7', color: '#166534', '& .MuiChip-icon': { color: '#166534' } },
  },
  outstanding: {
    label: 'Outstanding',
    Icon: OutstandingIcon,
    sx: { bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: '#92400e' } },
  },
};

// Icon plus text so the state does not rest on colour alone.
const VerificationChip = ({ verified }) => {
  const meta = VERIFICATION_META[verified ? 'verified' : 'outstanding'];
  const { Icon } = meta;
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 16 }} />}
      label={meta.label}
      sx={{ fontWeight: 600, ...meta.sx }}
    />
  );
};

// A stat card doubles as a filter toggle when onClick is supplied, same as the
// Reconciliation page. Kept as a real button so it is keyboard reachable and
// announces its pressed state.
const StatCard = ({ label, value, hint, accent, Icon, active, onClick, loading }) => {
  const content = (
    <Stack spacing={0.5} sx={{ width: '100%', textAlign: 'left' }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Icon sx={{ fontSize: 18, color: accent }} aria-hidden="true" />
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.2 }}>
          {label}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="text" width={80} sx={{ fontSize: '1.75rem' }} />
      ) : (
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}
        >
          {value}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ minHeight: 18 }}>
        {loading ? ' ' : hint}
      </Typography>
    </Stack>
  );

  const paperSx = {
    p: { xs: 1.5, md: 2 },
    height: '100%',
    borderColor: active ? accent : 'divider',
    // Keep the box the same size when active so the grid does not shift on toggle.
    boxShadow: active ? `inset 0 0 0 1px ${accent}` : 'none',
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
  };

  if (!onClick) {
    return (
      <Paper variant="outlined" sx={paperSx}>
        {content}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ ...paperSx, p: 0, overflow: 'hidden' }}>
      <ButtonBase
        onClick={onClick}
        aria-pressed={active}
        sx={{
          width: '100%',
          height: '100%',
          p: { xs: 1.5, md: 2 },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {content}
      </ButtonBase>
    </Paper>
  );
};

const matchesVerificationFilter = (pkg, filter) => {
  if (filter === 'verified') return isReturnVerified(pkg);
  if (filter === 'outstanding') return !isReturnVerified(pkg);
  return true;
};

const Returns = () => {
  // pages[i] = { items, hasMore }. Pages already fetched stay cached, so stepping
  // back (or forward again) costs no additional reads.
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCapped, setSearchCapped] = useState(false);
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  // Set by clicking the Verified / Outstanding cards. all | verified | outstanding
  const [verificationFilter, setVerificationFilter] = useState('all');

  // Aggregation-only totals: they answer the summary cards without reading a single
  // package document, and they are refreshed on mount rather than per page.
  const [stats, setStats] = useState({ returned: null, value: null, all: null, verified: null });
  const [statsLoading, setStatsLoading] = useState(true);

  // Verification state. processingId disables just the row being written, so the
  // rest of the table stays usable.
  const [usersMap, setUsersMap] = useState({});
  const [processingId, setProcessingId] = useState(null);
  // Toast content outlives its open flag so it stays mounted through the exit
  // transition, then clears on onExited.
  const [toast, setToast] = useState(null); // { message, severity, undo }
  const [toastOpen, setToastOpen] = useState(false);

  // Bumped by the Refresh button to re-run the current view from the server.
  const [refreshKey, setRefreshKey] = useState(0);

  // The committed search lives in the URL so results are shareable and the browser
  // back button undoes a search. The page index stays local: cursor pagination has no
  // cursor for an arbitrary page, so a deep link to "page 4" could not be honoured.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('q') || '';
  const [search, setSearch] = useState(searchTerm);

  // Keep the input in step when the term changes from outside the form, e.g. the
  // browser back button stepping out of a search.
  useEffect(() => {
    setSearch(searchTerm);
  }, [searchTerm]);

  // Last document snapshot of each fetched page, used as the startAfter cursor for
  // the page that follows it.
  const cursorsRef = useRef([]);

  const currentPage = pages[pageIndex];
  const hasMore = currentPage?.hasMore || false;

  const loadPage = useCallback(async (index) => {
    setLoading(true);
    setError(null);

    try {
      const constraints = [
        where('status', '==', 'returned'),
        orderBy(sortBy, sortOrder),
      ];

      const cursor = index > 0 ? cursorsRef.current[index - 1] : null;
      if (cursor) constraints.push(startAfter(cursor));

      // One extra doc tells us whether a further page exists without a second query.
      constraints.push(limit(rowsPerPage + 1));

      const snapshot = await getDocs(query(collection(db, 'packages'), ...constraints));
      const docs = snapshot.docs.slice(0, rowsPerPage);

      cursorsRef.current[index] = docs[docs.length - 1] || null;

      setPages(prev => {
        const next = [...prev];
        next[index] = {
          items: docs.map(d => ({ id: d.id, ...d.data() })),
          hasMore: snapshot.docs.length > rowsPerPage,
        };
        return next;
      });
      setPageIndex(index);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Could not load returned packages. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, rowsPerPage]);

  const loadSearchResults = useCallback(async (term) => {
    setLoading(true);
    setError(null);

    try {
      // Firestore only supports prefix matching, so we query the term as typed and
      // its uppercase form (generated tracking numbers are uppercase) and merge.
      //  is the high private-use code point that closes a prefix range.
      const variants = Array.from(new Set([term, term.toUpperCase()]));
      const snapshots = await Promise.all(variants.map(variant => getDocs(query(
        collection(db, 'packages'),
        where('status', '==', 'returned'),
        orderBy('trackingNumber'),
        where('trackingNumber', '>=', variant),
        where('trackingNumber', '<=', variant + ''),
        limit(rowsPerPage + 1)
      ))));

      const byId = new Map();
      snapshots.forEach(snapshot => {
        snapshot.docs.forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
      });

      const results = Array.from(byId.values());

      cursorsRef.current = [];
      setPages([{ items: results.slice(0, rowsPerPage), hasMore: false }]);
      setPageIndex(0);
      setSearchCapped(results.length > rowsPerPage);
    } catch (err) {
      console.error('Error searching packages:', err);
      setError('Could not run that search. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage]);

  // Outstanding returns carry no returnVerified field at all, and Firestore cannot
  // query for a missing field. So a card filter reads every returned package once
  // (still on the existing status + sort index), filters here, and splits the
  // result into pages - after which paging is served entirely from the cache.
  const loadFilteredResults = useCallback(async (filter) => {
    setLoading(true);
    setError(null);

    try {
      const snapshot = await getDocs(query(
        collection(db, 'packages'),
        where('status', '==', 'returned'),
        orderBy(sortBy, sortOrder),
      ));
      const matches = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(pkg => matchesVerificationFilter(pkg, filter));

      const chunked = [];
      for (let i = 0; i < matches.length; i += rowsPerPage) {
        chunked.push({
          items: matches.slice(i, i + rowsPerPage),
          hasMore: i + rowsPerPage < matches.length,
        });
      }

      cursorsRef.current = [];
      setPages(chunked.length ? chunked : [{ items: [], hasMore: false }]);
      setPageIndex(0);
    } catch (err) {
      console.error('Error filtering returned packages:', err);
      setError('Could not load returned packages. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, rowsPerPage]);

  // The single place a query is issued for the table body. It re-runs when the user,
  // the committed search, the card filter, the sort, or the page size changes - each
  // of which invalidates every cursor already collected.
  useEffect(() => {
    if (!user) return;

    cursorsRef.current = [];
    setPages([]);
    setPageIndex(0);
    setSearchCapped(false);

    // A search is already a single page, so the card filter is applied to it in
    // `rows` rather than needing a query of its own.
    if (searchTerm) {
      loadSearchResults(searchTerm);
    } else if (verificationFilter !== 'all') {
      loadFilteredResults(verificationFilter);
    } else {
      loadPage(0);
    }
  }, [user, searchTerm, verificationFilter, refreshKey, loadPage, loadSearchResults, loadFilteredResults]);

  // Aggregation queries: counts and a sum computed server-side, so the summary
  // cards cost no document reads no matter how large the collection grows. They are
  // issued separately and settled independently - a sum() over a filtered query
  // needs its own composite index, and a missing one must not take the counts down
  // with it. Anything that fails stays null and its card reads as unavailable.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const returnedQuery = query(collection(db, 'packages'), where('status', '==', 'returned'));
    // Only verified is counted: an unverified package has no returnVerified field
    // at all, and an equality filter never matches a missing field. Outstanding is
    // therefore derived as returned - verified rather than queried.
    const verifiedQuery = query(
      collection(db, 'packages'),
      where('status', '==', 'returned'),
      where('returnVerified', '==', true),
    );

    setStatsLoading(true);
    Promise.allSettled([
      getCountFromServer(returnedQuery),
      getAggregateFromServer(returnedQuery, { value: sum('total') }),
      getCountFromServer(collection(db, 'packages')),
      getCountFromServer(verifiedQuery),
    ])
      .then(([returnedRes, valueRes, allRes, verifiedRes]) => {
        if (cancelled) return;
        [returnedRes, valueRes, allRes, verifiedRes].forEach((res) => {
          if (res.status === 'rejected') console.error('Error loading return totals:', res.reason);
        });
        setStats({
          returned: returnedRes.status === 'fulfilled' ? returnedRes.value.data().count : null,
          value: valueRes.status === 'fulfilled' ? valueRes.value.data().value : null,
          all: allRes.status === 'fulfilled' ? allRes.value.data().count : null,
          verified: verifiedRes.status === 'fulfilled' ? verifiedRes.value.data().count : null,
        });
        setStatsLoading(false);
      });

    return () => { cancelled = true; };
  }, [user, refreshKey]);

  // Names for the "verified by" line. Small collection, fetched once per load
  // rather than per row.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    getDocs(collection(db, 'users'))
      .then((snap) => {
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => { map[d.id] = d.data(); });
        setUsersMap(map);
      })
      .catch((err) => console.error('Error loading users:', err));

    return () => { cancelled = true; };
  }, [user, refreshKey]);

  const displayName = useCallback((uid) => {
    if (!uid) return null;
    const u = usersMap[uid];
    return u?.name || u?.displayName || u?.email || uid;
  }, [usersMap]);

  const showToast = useCallback((next) => {
    setToast(next);
    setToastOpen(true);
  }, []);

  // Unlike Reconciliation, which holds every row in memory, this page caches
  // server-fetched pages. The written row is patched in place across whichever
  // cached pages hold it, so the table reflects the change without re-querying
  // and without discarding the cursors already collected.
  const setReturnVerification = useCallback(async (pkg, verified, { isUndo = false } = {}) => {
    if (!user || !pkg.id) return;
    setProcessingId(pkg.id);
    try {
      await updateDoc(doc(db, 'packages', pkg.id), verified
        ? {
            returnVerified: true,
            returnVerifiedAt: serverTimestamp(),
            returnVerifiedBy: user.uid,
          }
        : {
            // Cleared rather than set false, so "never verified" and "verification
            // withdrawn" look identical - there is no half-state to explain.
            returnVerified: deleteField(),
            returnVerifiedAt: deleteField(),
            returnVerifiedBy: deleteField(),
          });

      const patch = verified
        ? { returnVerified: true, returnVerifiedAt: new Date(), returnVerifiedBy: user.uid }
        : { returnVerified: false, returnVerifiedAt: null, returnVerifiedBy: null };

      setPages(prev => prev.map(page => (page
        ? { ...page, items: page.items.map(item => (item.id === pkg.id ? { ...item, ...patch } : item)) }
        : page)));

      // Keep the cards live without paying for another aggregation round trip.
      setStats(prev => ({
        ...prev,
        verified: prev.verified == null ? null : prev.verified + (verified ? 1 : -1),
      }));

      showToast({
        severity: 'success',
        message: verified
          ? `Return for ${pkg.recipient || pkg.trackingNumber || 'package'} marked verified.`
          : `Return for ${pkg.recipient || pkg.trackingNumber || 'package'} marked outstanding.`,
        // A mis-click is one click to walk back, same as the payments flow.
        undo: isUndo ? null : () => setReturnVerification(pkg, !verified, { isUndo: true }),
      });
    } catch (err) {
      console.error('Error updating return verification:', err);
      showToast({
        severity: 'error',
        message: 'Could not update this return. Please try again.',
      });
    } finally {
      setProcessingId(null);
    }
  }, [user, showToast]);

  // Search returns a single page of matches, so re-ordering them is a client-side
  // sort of at most one page - no extra query.
  //
  // The card filter is re-applied here as well, so a row verified while viewing
  // Outstanding (or withdrawn while viewing Verified) leaves the view straight
  // away, the way it does on the Reconciliation page.
  const rows = useMemo(() => {
    const items = (currentPage?.items || [])
      .filter(pkg => matchesVerificationFilter(pkg, verificationFilter));
    if (!searchTerm) return items;
    const dir = sortOrder === 'asc' ? 1 : -1;
    const value = (pkg) => (sortBy === 'trackingNumber'
      ? (pkg.trackingNumber || '').toLowerCase()
      : toDate(pkg.createdAt)?.getTime() ?? null);
    return [...items].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av === bv) return 0;
      return av > bv ? dir : -dir;
    });
  }, [currentPage, searchTerm, verificationFilter, sortBy, sortOrder]);

  const handleSort = (field) => {
    if (!SORTABLE_FIELDS.includes(field)) return;
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'trackingNumber' ? 'asc' : 'desc');
    }
  };

  const handleChangePage = (event, nextIndex) => {
    if (nextIndex < 0 || loading) return;
    // A page already in the cache is shown without going back to the server.
    if (pages[nextIndex]) {
      setPageIndex(nextIndex);
      return;
    }
    loadPage(nextIndex);
  };

  const commitSearch = (term) => {
    setSearchParams(term ? { q: term } : {});
  };

  const handleSearch = (e) => {
    e.preventDefault();
    commitSearch(search.trim());
  };

  const handleClearSearch = () => {
    setSearch('');
    commitSearch('');
  };

  // Clicking the active card again clears the filter.
  const toggleVerificationCard = (filter) => {
    setVerificationFilter(prev => (prev === filter ? 'all' : filter));
  };

  // The Total Returned card is the "show everything" control: it drops both the
  // card filter and any search.
  const handleShowAll = () => {
    setVerificationFilter('all');
    handleClearSearch();
  };

  const filterActive = verificationFilter !== 'all';
  const filterLabel = VERIFICATION_META[verificationFilter]?.label.toLowerCase();
  // In filter mode every page is already in the cache, so the total is known.
  const filteredTotal = pages.reduce(
    (n, page) => n + (page?.items.filter(pkg => matchesVerificationFilter(pkg, verificationFilter)).length || 0),
    0,
  );
  const listTotal = searchTerm ? rows.length : filterActive ? filteredTotal : stats.returned;

  const rangeStart = rows.length === 0 ? 0 : pageIndex * rowsPerPage + 1;
  const rangeEnd = pageIndex * rowsPerPage + rows.length;
  const returnRate = stats.all ? (stats.returned / stats.all) * 100 : null;
  // Derived rather than queried — see the aggregation effect for why.
  const outstandingCount = stats.returned === null || stats.verified === null
    ? null
    : stats.returned - stats.verified;

  // The old Status column showed a constant "Returned" chip on every row - every
  // row here is a return by definition - so that slot now carries the one piece
  // of per-row state that actually varies.
  const columns = [
    { id: 'recipient', label: 'Recipient' },
    { id: 'trackingNumber', label: 'Tracking #', sortable: true },
    { id: 'verification', label: 'Verification' },
    { id: 'destination', label: 'Destination' },
    { id: 'createdAt', label: 'Created', sortable: true },
    { id: 'updatedAt', label: 'Last Updated' },
    { id: 'verifiedAt', label: 'Verified At' },
    { id: 'actions', label: 'Actions', align: 'right' },
  ];

  const showSkeleton = loading && rows.length === 0;

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', lg: 1280, xl: 1600 }, p: { xs: 2, md: 3 }, px: { lg: 4 } }}>
        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: { xs: 2, md: 3 } }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              component="img"
              src={logo}
              alt=""
              sx={{ height: { xs: 48, md: 64 }, width: 'auto', objectFit: 'contain' }}
            />
            <Box>
              <Typography variant="h5" fontWeight={600} color="text.primary">
                Returned Packages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Every package currently marked returned, newest first. Verify a return
                once you have confirmed the goods came back.
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loading}
            sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setRefreshKey(k => k + 1)}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Summary. Three server-side aggregations, run once per load rather than
            per page, so paging never recomputes them. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
            gap: { xs: 1.5, md: 2 },
            mb: { xs: 2, md: 3 },
          }}
        >
          <StatCard
            label="TOTAL RETURNED"
            value={stats.returned ?? '—'}
            hint={stats.returned === null
              ? 'Total unavailable'
              : filterActive || searchTerm ? 'Click to clear filters' : 'Packages currently marked returned'}
            accent="#b45309"
            Icon={ReturnedIcon}
            active={!filterActive && !searchTerm}
            onClick={handleShowAll}
            loading={statsLoading}
          />
          <StatCard
            label="VERIFIED"
            value={stats.verified ?? '—'}
            hint={stats.verified === null
              ? 'Total unavailable'
              : verificationFilter === 'verified' ? 'Filtering by verified' : 'Returns confirmed received back'}
            accent="#15803d"
            Icon={VerifiedIcon}
            active={verificationFilter === 'verified'}
            onClick={() => toggleVerificationCard('verified')}
            loading={statsLoading}
          />
          <StatCard
            label="OUTSTANDING"
            value={outstandingCount ?? '—'}
            hint={outstandingCount === null
              ? 'Total unavailable'
              : verificationFilter === 'outstanding' ? 'Filtering by outstanding' : 'Returns still to be confirmed'}
            accent="#b45309"
            Icon={OutstandingIcon}
            active={verificationFilter === 'outstanding'}
            onClick={() => toggleVerificationCard('outstanding')}
            loading={statsLoading}
          />
          <StatCard
            label="RETURNED VALUE"
            value={stats.value === null ? '—' : formatCurrency(stats.value)}
            hint={stats.value === null ? 'Total unavailable' : 'Combined total of returned packages'}
            accent="#1d4ed8"
            Icon={ValueIcon}
            loading={statsLoading}
          />
          <StatCard
            label="RETURN RATE"
            value={returnRate === null ? '—' : `${returnRate.toFixed(1)}%`}
            hint={stats.all ? `${stats.returned} of ${stats.all} packages` : 'No packages yet'}
            accent="#475569"
            Icon={RateIcon}
            loading={statsLoading}
          />
        </Box>

        {/* Search. Submitted rather than live: each search is a query, so it runs
            when the user asks for it. */}
        <Paper variant="outlined" component="form" onSubmit={handleSearch} sx={{ p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
            <TextField
              id="returns-search"
              label="Search by tracking number"
              placeholder="e.g. TRK-20260101"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              fullWidth
              helperText={
                searchTerm
                  ? `Showing tracking numbers that start with "${searchTerm}"${
                      searchCapped ? ` — first ${rowsPerPage} matches only, type more characters to narrow it down` : ''
                    }`
                  : 'Matches the start of a tracking number, so enter it from the beginning.'
              }
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack direction="row" spacing={1} sx={{ pt: { sm: 0.25 } }}>
              <Button type="submit" variant="contained" disableElevation sx={{ whiteSpace: 'nowrap' }}>
                Search
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                startIcon={<ClearIcon />}
                onClick={handleClearSearch}
                disabled={!searchTerm && !search}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Table */}
        {!showSkeleton && rows.length === 0 && !error ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            {filterActive && !searchTerm ? (
              <>
                <Typography color="text.primary" gutterBottom>
                  No {filterLabel} returns.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={() => setVerificationFilter('all')}
                  sx={{ mt: 1 }}
                >
                  Show all returns
                </Button>
              </>
            ) : searchTerm ? (
              <>
                <Typography color="text.primary" gutterBottom>
                  No {filterActive ? `${filterLabel} ` : ''}returned package starts with "{searchTerm}".
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Check the first few characters, or clear the search to browse all returns.
                </Typography>
                <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>
                  Clear search
                </Button>
              </>
            ) : (
              <Typography color="text.secondary">
                No packages have been marked as returned yet.
              </Typography>
            )}
          </Paper>
        ) : (
          <>
            {/* Rows stay on screen while the next page loads, so the table does not
                collapse to a spinner and shift the page around. */}
            <TableContainer
              component={Paper}
              variant="outlined"
              aria-busy={loading}
              sx={{ opacity: loading && rows.length > 0 ? 0.6 : 1, transition: 'opacity 160ms ease' }}
            >
              <Table size="small" sx={{ minWidth: 1150 }}>
                <caption style={visuallyHidden}>
                  Returned packages, page {pageIndex + 1}
                </caption>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 600, whiteSpace: 'nowrap', py: 1.25 } }}>
                    {columns.map((col) => (
                      // sortDirection is what puts aria-sort on the header cell - the
                      // sort label alone does not announce the sorted column.
                      <TableCell
                        key={col.id}
                        align={col.align}
                        sortDirection={col.sortable && sortBy === col.id ? sortOrder : false}
                      >
                        {col.sortable ? (
                          <TableSortLabel
                            active={sortBy === col.id}
                            direction={sortBy === col.id ? sortOrder : 'asc'}
                            onClick={() => handleSort(col.id)}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showSkeleton
                    ? // Skeleton rows hold the table's height so the page does not
                      // jump when the first page lands.
                      Array.from({ length: 8 }).map((_, i) => (
                        <TableRow key={`skeleton-${i}`}>
                          {columns.map((col) => (
                            <TableCell key={col.id} align={col.align} sx={{ py: 1.25 }}>
                              <Skeleton variant="text" width={col.id === 'recipient' ? 150 : 90} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : rows.map((pkg) => {
                        const created = formatDateTime(pkg.createdAt);
                        const updated = formatDateTime(pkg.updatedAt);
                        const verified = isReturnVerified(pkg);
                        const verifiedOn = formatDateTime(pkg.returnVerifiedAt);
                        const verifiedByName = displayName(pkg.returnVerifiedBy);
                        const isProcessing = processingId === pkg.id;
                        return (
                          <TableRow
                            key={pkg.id || pkg.trackingNumber}
                            hover
                            onClick={() => navigate(`/package/${pkg.id}`)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell sx={{ py: 1.25 }}>
                              {/* Real link: gives keyboard users a focusable target for
                                  the row click, and supports open-in-new-tab. */}
                              <Link
                                component={RouterLink}
                                to={`/package/${pkg.id}`}
                                onClick={(e) => e.stopPropagation()}
                                underline="hover"
                                variant="body2"
                                fontWeight={500}
                                sx={{ color: 'text.primary' }}
                              >
                                {pkg.recipient || 'Unknown recipient'}
                              </Link>
                            </TableCell>

                            <TableCell sx={{ py: 1.25 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                              >
                                {pkg.trackingNumber || '—'}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.25 }}>
                              <VerificationChip verified={verified} />
                            </TableCell>

                            <TableCell sx={{ py: 1.25 }}>
                              <Typography variant="body2">{pkg.destination || 'N/A'}</Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                              {created ? (
                                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {created}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                  <Box component="span" sx={visuallyHidden}>No created date</Box>
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                              {updated ? (
                                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {updated}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                  <Box component="span" sx={visuallyHidden}>Never updated</Box>
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ py: 1.25, whiteSpace: 'nowrap' }}>
                              {verifiedOn ? (
                                <>
                                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {verifiedOn}
                                  </Typography>
                                  {verifiedByName && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      by {verifiedByName}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                  <Box component="span" sx={visuallyHidden}>Not verified</Box>
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="right" sx={{ py: 1.25 }}>
                              {/* stopPropagation throughout: the whole row is a
                                  link to the package, and verifying must not
                                  navigate away from the table. */}
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {verified ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setReturnVerification(pkg, false);
                                    }}
                                    disabled={isProcessing}
                                    startIcon={isProcessing
                                      ? <CircularProgress size={14} color="inherit" />
                                      : <OutstandingIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                      whiteSpace: 'nowrap',
                                      color: '#92400e',
                                      borderColor: '#fbbf24',
                                      '&:hover': { borderColor: '#b45309', bgcolor: '#fffbeb' },
                                    }}
                                  >
                                    {isProcessing ? 'Updating' : 'Mark Outstanding'}
                                    <Box component="span" sx={visuallyHidden}>
                                      {` return for ${pkg.trackingNumber || pkg.recipient || ''}`}
                                    </Box>
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disableElevation
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setReturnVerification(pkg, true);
                                    }}
                                    disabled={isProcessing}
                                    startIcon={isProcessing
                                      ? <CircularProgress size={14} color="inherit" />
                                      : <VerifiedIcon sx={{ fontSize: 16 }} />}
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    {isProcessing ? 'Verifying' : 'Verify'}
                                    <Box component="span" sx={visuallyHidden}>
                                      {` return for ${pkg.trackingNumber || pkg.recipient || ''}`}
                                    </Box>
                                  </Button>
                                )}
                                <Tooltip title="Open package details">
                                  <Button
                                    component={RouterLink}
                                    to={`/package/${pkg.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    sx={{ whiteSpace: 'nowrap' }}
                                  >
                                    View
                                    <Box component="span" sx={visuallyHidden}>
                                      {` package ${pkg.trackingNumber || pkg.recipient || ''}`}
                                    </Box>
                                  </Button>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Cursor pagination: only prev/next are offered, because there is no
                cursor for an arbitrary page. A visited page comes from the cache. */}
            <TablePagination
              component="div"
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              count={listTotal ?? -1}
              rowsPerPage={rowsPerPage}
              page={pageIndex}
              onPageChange={handleChangePage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              labelRowsPerPage="Rows per page:"
              getItemAriaLabel={(type) => `Go to ${type} page of returned packages`}
              // These spread over MUI's own disabled logic, which is what we want:
              // the cursor knows whether another page exists, and a search always
              // returns a single page. Both beat anything inferred from the count.
              slotProps={{
                actions: {
                  nextButton: { disabled: loading || !hasMore },
                  previousButton: { disabled: loading || pageIndex === 0 },
                },
              }}
              sx={{
                // Wrap rather than scroll sideways on a narrow phone, and keep the
                // prev/next buttons at a comfortable touch target.
                '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, px: { xs: 1, sm: 2 } },
                '& .MuiTablePagination-actions button': { p: 1.5 },
              }}
            />
          </>
        )}

        {/* One atomic status message: a bare changing number read on its own tells a
            screen reader user nothing about what moved. */}
        <Typography
          role="status"
          aria-atomic="true"
          variant="caption"
          color="text.secondary"
          align="center"
          display="block"
          sx={{ mt: 2 }}
        >
          {loading
            ? 'Loading returned packages...'
            : searchTerm
              ? `Found ${rows.length} matching ${filterActive ? `${filterLabel} ` : ''}package${rows.length === 1 ? '' : 's'}`
              : rows.length === 0
                ? `Showing 0 ${filterActive ? `${filterLabel} ` : ''}returned packages`
                : `Showing ${rangeStart}-${rangeEnd}${listTotal != null ? ` of ${listTotal}` : ''} ${filterActive ? `${filterLabel} ` : ''}returned packages`}
        </Typography>
      </Box>

      <Snackbar
        open={toastOpen}
        // An undoable action gets longer than the usual 3-5s so the undo is
        // genuinely reachable before the toast leaves.
        autoHideDuration={toast?.undo ? 8000 : 5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{ transition: { onExited: () => setToast(null) } }}
      >
        <Alert
          severity={toast?.severity || 'success'}
          variant="filled"
          onClose={() => setToastOpen(false)}
          action={toast?.undo ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const undo = toast.undo;
                setToastOpen(false);
                undo();
              }}
            >
              Undo
            </Button>
          ) : undefined}
        >
          {toast?.message || ''}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Returns;
